import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import NoteBox from "../../components/controls/NoteBox";
import { sendBecomeProviderRequest } from "../../db/BecomeProvider";
import Autocomplete from "../../components/Places/PlacesAutocomplete";
import { fetchUser } from "../../db/authHelper";
import FormInput from "../../components/controls/FormInput";
import { useNavigate } from "react-router-dom";

import {
  BuildingOfficeIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  IdentificationIcon,
  PhoneIcon,
  UserCircleIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/20/solid";
import FormTextarea from "../../components/controls/FormTextArea";
import { useAuth } from "../../contexts/AuthContext";

const BecomeProviderPage = () => {
  const { t } = useTranslation();
  const [autocomplete, setAutocomplete] = useState(null);
  const { currentUser } = useAuth();
  const userId = currentUser?.id;
  const [formData, setFormData] = useState({
    companyName: "",
    productsAndServices: "",
    priceRange: "",
    companyAddress: "",
    contactPerson: "",
    phoneNumber: "",
    neqNumber: "",
    contactEmail: "",
    userId: "",
    website: "",
    location: {
      formattedAddress: "",
      latitude: null,
      longitude: null,
      regionId: 0,
    },
    submittedAt: new Date(),
    subscriptionTier: 0,
  });

  const navigate = useNavigate();

  useEffect(() => {
    const fetchAndSetUser = async () => {
      const user = await fetchUser();

      if (user) {
        setFormData((prevState) => ({
          ...prevState,
          userId: user.id,
        }));

        const isProvider = user.user_roles?.some((role) => role.role_id === 2);

        if (isProvider) {
          navigate("/provider-dashboard");
        }
      }
    };

    fetchAndSetUser();
  }, []);

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const validatePhoneNumber = (phoneNumber) => {
    const phoneRegex =
      /^(1\s?)?(\([2-9][0-9]{2}\)|[2-9][0-9]{2})([-\s.]?)\d{3}([-\s.]?)\d{4}$/;
    return phoneRegex.test(phoneNumber.trim());
  };

  const handleChange = (name, value) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: name === "location" ? { ...prevFormData[name], ...value } : value,
    }));

    if (
      (typeof value === "string" && value.trim() !== "") ||
      name === "website" ||
      name === "location"
    ) {
      setErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };

        if (name === "location") {
          updatedErrors.companyAddress = "";
        } else if (value.trim() !== "" || name === "website") {
          updatedErrors[name] = "";
        }
        return updatedErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (formData.companyName.trim() === "") {
      newErrors.company_name = t("errors.companyNameRequired");
    }
    if (formData.productsAndServices.trim() === "") {
      newErrors.products_and_services = t("errors.productsAndServicesRequired");
    }
    if (formData.priceRange.trim() === "") {
      newErrors.price_range = t("errors.priceRangeRequired");
    }
    if (formData.contactPerson.trim() === "") {
      newErrors.contact_person = t("errors.contactPersonRequired");
    }
    if (
      formData.phoneNumber.trim() === "" ||
      !validatePhoneNumber(formData.phoneNumber)
    ) {
      newErrors.phone_number = t("errors.phoneNumberInvalid");
    }
    if (
      formData.contactEmail.trim() === "" ||
      !/\S+@\S+\.\S+/.test(formData.contactEmail)
    ) {
      newErrors.contact_email = t("errors.contactEmailInvalid");
    }

    if (
      !formData.location ||
      !formData.location.formattedAddress ||
      !formData.location.latitude ||
      !formData.location.longitude
    ) {
      newErrors.companyAddress = t("errors.companyAddressRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    const fetchAndSetUser = async () => {
      const user = await fetchUser();

      if (user) {
        setFormData((prevState) => ({
          ...prevState,
          userId: user.id,
        }));
      }
    };

    fetchAndSetUser();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      const payload = {
        ...formData,
        userId: userId,
      };
      const result = await sendBecomeProviderRequest(payload);
      if (result.success) {
        setSubmitted(true);
      } else {
        setServerErrors(result.message);
      }
    }
  };

  const setServerErrors = async (message) => {
    const newErrors = {};
    newErrors.serverError = message;
    setErrors(newErrors);
  };

  if (submitted) {
    return (
      <div className="p-4 text-center">
        <h1 className="text-3xl font-bold text-green-600">
          {t("becomeProvider.successMessageTitle")}
        </h1>
        <p className="mt-4 text-gray-700">
          {t("becomeProvider.successMessageDescription")}
        </p>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-50 text-gray-800">
      <div className="w-full max-w-5xl p-6 bg-white shadow-md rounded-md">
        <h1 className="text-3xl font-bold mb-2 text-center">
          {t("becomeProvider.pageTitle")}
        </h1>
        <p className="mb-6 text-gray-600 text-center">
          {t("becomeProvider.formDescription")}
        </p>

        <NoteBox
          title={t("becomeProvider.noteTitle")}
          content={t("becomeProvider.noteContent")}
        />

        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-6"
        >
          {/* Nom de l'entreprise */}
          <FormInput
            label={t("becomeProvider.companyNameLabel")}
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
            required={true}
            placeholder={t("becomeProvider.companyNamePlaceholder")}
            error={errors.company_name}
            icon={<BuildingOfficeIcon className="w-5 h-5 text-gray-500" />}
          />

          {/* # NEQ */}
          <FormInput
            label={t("becomeProvider.neqNumberLabel")}
            name="neqNumber"
            value={formData.neqNumber}
            onChange={handleChange}
            placeholder={t("becomeProvider.neqNumberPlaceholder")}
            error={errors.neq_number}
            icon={<IdentificationIcon className="w-5 h-5 text-gray-500" />}
          />

          {/* Personne ressource */}
          <FormInput
            label={t("becomeProvider.contactPersonLabel")}
            name="contactPerson"
            value={formData.contactPerson}
            onChange={handleChange}
            placeholder={t("becomeProvider.contactPersonPlaceholder")}
            error={errors.contact_person}
            required
            icon={<UserCircleIcon className="w-5 h-5 text-gray-500" />}
          />

          {/* Numéro de téléphone */}
          <FormInput
            label={t("becomeProvider.phoneNumberLabel")}
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleChange}
            placeholder={t("becomeProvider.phoneNumberPlaceholder")}
            type="tel"
            error={errors.phone_number}
            required
            icon={<PhoneIcon className="w-5 h-5 text-gray-500" />}
          />

          {/* Adresse courriel */}
          <FormInput
            label={t("becomeProvider.contactEmailLabel")}
            name="contactEmail"
            value={formData.contactEmail}
            onChange={handleChange}
            placeholder={t("becomeProvider.contactEmailPlaceholder")}
            type="email"
            error={errors.contact_email}
            required
            icon={<EnvelopeIcon className="w-5 h-5 text-gray-500" />}
          />

          {/* Adresse du site web */}
          <FormInput
            label={t("becomeProvider.websiteLabel")}
            name="website"
            value={formData.website}
            onChange={handleChange}
            placeholder={t("becomeProvider.websitePlaceholder")}
            icon={<GlobeAltIcon className="w-5 h-5 text-gray-500" />}
          />

          {/* Adresse de l'entreprise */}
          <div>
            <Autocomplete
              onSelect={(e) => {
                handleChange("location", e);
                handleChange("companyAddress", e.formattedAddress);
              }}
              initialAddress=""
              showOptional={false}
              customClassName={`${
                errors.companyAddress ? "border-red-500" : ""
              }`}
            />
            {errors.companyAddress && (
              <p className="text-red-500 text-sm mt-1">
                {errors.companyAddress}
              </p>
            )}
            <p className="text-muted text-sm mt-1">
              {t("becomeProvider.companyAddressHint")}
            </p>
          </div>

          {/* Fourchette de prix */}
          <FormInput
            label={t("becomeProvider.priceRangeLabel")}
            name="priceRange"
            value={formData.priceRange}
            onChange={handleChange}
            placeholder={t("becomeProvider.priceRangePlaceholder")}
            error={errors.price_range}
            required={true}
            icon={<CurrencyDollarIcon className="w-5 h-5 text-gray-500" />}
          />

          {/* Produits et services */}
          <div>
            <FormTextarea
              name="productsAndServices"
              label={t("becomeProvider.productsAndServicesLabel")}
              value={formData.productsAndServices}
              onChange={handleChange}
              required={true}
              placeholder={t("becomeProvider.productsAndServicesPlaceholder")}
              className={`w-full border border-gray-300 rounded-lg px-4  focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary ${
                errors.products_and_services ? "border-red-500" : ""
              }`}
            />
            <p className="text-muted text-sm">
              {t("becomeProvider.productsAndServicesHint")}
            </p>
            {errors.productsAndServices && (
              <p className="text-red-500 text-sm mt-1">
                {errors.products_and_services}
              </p>
            )}
          </div>

          {/* Bouton de soumission */}
          <div className="sm:col-span-2 flex justify-center">
            <button
              type="submit"
              className="w-1/3 bg-primary text-white px-4 py-2 rounded-full hover:bg-accent items-center"
            >
              {t("becomeProvider.submitButton")}
            </button>
          </div>
        </form>
        {errors.serverError && (
          <p className="text-red-500 text-sm mt-1 text-center w-full flex-1">
            {errors.serverError}
          </p>
        )}
      </div>
    </div>
  );
};

export default BecomeProviderPage;
